import { ensureCsrfToken } from './auth';
import { isSessionBlocked } from '@/core';
import { clearSession, createHttpClient, getSession, loadEnv, setSession } from '@/core';
import { z } from 'zod';

const SessionResponseSchema = z.object({
  accessToken: z.string().min(1),
  expiresAt: z.union([z.number(), z.string().datetime({ offset: true })]),
  // CSRF devolvido no corpo do refresh — renovado em memória junto com o token. Campo garantido
  // pelo contrato (BrowserSessionResponse.Csrf, não-nulo), portanto obrigatório.
  csrf: z.string().min(1),
});

/**
 * CSRF do BFF, ecoado em header nas mutações — o token de acesso nunca sai da memória.
 *
 * A fonte canônica é a sessão em memória: a SPA roda em origem distinta do BFF (Pages/Dokploy) e
 * não consegue ler `document.cookie` de lá; o valor chega no corpo de `/auth/session`,
 * `/auth/refresh` e `/auth/csrf`. O cookie `dt_csrf` fica como fallback para deploy same-site.
 */
export function csrfToken(): string | null {
  return getSession().csrf;
}

export function toEpochMs(value: number | string): number {
  return typeof value === 'number' ? value : Date.parse(value);
}

/**
 * C4 — composição do httpClient de C2 com a sessão e a config deste app.
 * A troca e a renovação acontecem no BFF, com o refresh token em cookie HttpOnly.
 */
/**
 * Renovação de sessão compartilhada — usada pelo cliente principal e pelo cliente de pagamentos
 * (mesmo cookie HttpOnly do BFF, mesma sessão em memória).
 */
export async function refreshSession(): Promise<void> {
  if (isSessionBlocked()) throw new Error('Saída pendente. Entre novamente.');
  const env = loadEnv();
  const response = await fetch(`${env.VITE_BFF_BASE_URL}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken() ?? '' },
  });
  if (!response.ok) {
    clearSession();
    throw new Error('Sessão expirada. Entre novamente.');
  }
  const parsed = SessionResponseSchema.safeParse(await response.json());
  if (!parsed.success) {
    clearSession();
    throw new Error('Resposta de sessão inválida.');
  }
  setSession({
    ...getSession(),
    accessToken: parsed.data.accessToken,
    expiresAt: toEpochMs(parsed.data.expiresAt),
    csrf: parsed.data.csrf,
  });
}

export const httpClient = createHttpClient({
  baseUrl: loadEnv().VITE_API_BASE_URL,
  getAccessToken: () => getSession().accessToken,
  refreshSession,
  getCsrfToken: csrfToken,
  canSendRequest: (path) => !isSessionBlocked() || path.startsWith('/auth/password/') || path === '/equipment-categories' || path === '/auth/register/clinic',
  ensureCsrfToken: async () => {
    if (getSession().user !== null || getSession().accessToken !== null) await ensureCsrfToken();
  },
  onSessionExpired: () => {
    clearSession();
  },
});
